import type {
  EditarRenglonRequest, EditarRenglonResponse, HojaIncompleta, RenglonOrigen,
  EstructuraDudosaGuardada,
} from '@supernova/shared';
import { apiFetch } from './client.js';

export type QuoteStatus =
  | 'draft' | 'queued' | 'running' | 'paused'
  | 'done' | 'completed' | 'failed' | 'canceled';

export type QuoteControl = 'run' | 'pause' | 'stop';

export interface Quote {
  id: string;
  tenantId: string;
  projectId: string;
  sourceFileId: string | null;
  name: string;
  status: QuoteStatus;
  control: QuoteControl;
  conceptsTotal: number;
  conceptsProcessed: number;
  conceptsPriced: number;
  excelStorageKey: string | null;
  excelSizeBytes: number | null;
  excelGeneratedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  options: Record<string, unknown> | null;
  ownerUserId: string | null;
  /** True si es la cotización del proyecto de ejemplo (demo, solo lectura). */
  isExample?: boolean;
  catalogIds: string[];
  createdAt: string;
  updatedAt: string;
  opusDownloadedAt: string | null;
  opusImportedAt: string | null;
  opusObraName: string | null;
  llmInputTokens: number;
  llmOutputTokens: number;
  /** Verificación de integridad de la extracción (capa 1) — null si no corrió. */
  integrity: QuoteIntegrity | null;
  /** Por qué la hoja lleva claves propias en vez de las del documento (null = usa las del
   *  documento). Ya viene redactado para mostrarse tal cual. */
  clavesAviso?: string | null;
  /** Renglones que el documento muestra y no se pudieron leer: entraron a la hoja
   *  marcados (null = la hoja quedó completa). El `aviso` ya viene redactado. */
  hojaIncompleta?: HojaIncompleta | null;
  /** La hoja salió con la estructura de partidas en duda (null = sin sospecha). Los
   *  renglones NO están en duda: eso se verifica aparte. El `aviso` ya viene redactado. */
  estructuraDudosa?: EstructuraDudosaGuardada | null;
  /** Moneda en la que está este presupuesto (ISO 4217) o `null` si no se sabe.
   *  Es la MISMA con la que se decidieron los precios. Ver `lib/money.ts`. */
  moneda?: string | null;
  /** De dónde salió: 'contexto' | 'catalogos' | 'desconocida'. */
  monedaOrigen?: 'contexto' | 'catalogos' | 'desconocida';
}

/** Cruce señal-del-documento-fuente vs conceptos extraídos (migr 048). */
export interface QuoteIntegrity {
  method: 'xlsx-rows' | 'text-rows' | 'none';
  estimatedQuotableRows: number | null;
  sequenceGaps?: number[];
  sequenceMax?: number;
  approximate: boolean;
  extractedConcepts: number;
  verdict: 'ok' | 'warning' | 'no-signal';
}

export interface QuoteConceptInsumo {
  tipo: 'Simple' | 'Compuesto';
  clave: string;
  descripcion: string;
  unidad: string;
  costoUnitario: number;
  cantidad: number;
  total: number;
  /** Código de tipo de recurso OPUS (1=material, 2=mano de obra, 3=herramienta,
   *  4/5=equipo, 6=auxiliar, 7=concepto). Para agrupar el análisis del match. */
  tipoCodigo?: number;
}

export interface QuoteConcept {
  id: string;
  quoteId: string;
  idx: number;
  clave: string | null;
  descripcion: string;
  unidad: string;
  cantidad: number;
  precioUnitario: number;
  importe: number;
  confianza: 'alta' | 'media' | 'sin_candidato' | 'sintetica';
  fuente: string | null;
  nota: string | null;
  /** sin_candidato CON candidatos guardados: la IA puede validarlos. */
  hasCandidates?: boolean;
  catalogIdOrigen: string | null;
  claveCatalogo: string | null;
  descripcionCatalogo: string | null;
  unidadCatalogo: string | null;
  score: number | null;
  insumos: QuoteConceptInsumo[];
  status: 'pending' | 'priced' | 'failed' | 'manual' | 'skipped';
  /** Selección del cotizador: false = no se cotiza, se exporta "sin análisis". */
  cotizar: boolean;
  processedAt: string | null;
  kind: 'group' | 'concept';
  parentIdx: number | null;
  parentClave: string | null;
  apuIaJobRunId: string | null;
  apuIaContextOutdated: boolean;
  /** Toggle "Cotizar con IA": null = default por regla (sin_candidato ON / match OFF). */
  apuIaRequested: boolean | null;
  /** Estado de revisión del análisis con IA: pending/approved/rejected/edited/null. */
  apuIaReviewStatus?: 'pending' | 'approved' | 'rejected' | 'edited' | null;
  /** Ya tiene un análisis con IA generado → se puede cambiar la fuente sin regenerar. */
  hasApuIaMatrix: boolean;
  /**
   * LOS DOS PRECIOS DEL RENGLÓN. `precioUnitario` es el que cuenta hoy en el total; estos
   * son el otro, para poder mostrar los dos y elegir sin pagar nada:
   *  · `precioCatalogoGuardado` — el que se encontró en los catálogos del cliente, guardado
   *    antes de que el análisis con IA lo reemplazara. 0 o null = el renglón nunca tuvo
   *    equivalente y no hay a dónde volver.
   *  · `precioIa` — el que armó la inteligencia artificial, esté activo o no.
   * Las reglas de qué se puede elegir viven en `@supernova/shared` (precioDelRenglon).
   */
  precioCatalogoGuardado?: number | null;
  importeCatalogoGuardado?: number | null;
  confianzaCatalogoGuardada?: string | null;
  scoreCatalogoGuardado?: number | null;
  precioIa?: number | null;
  importeIa?: number | null;
  /**
   * De dónde salió el renglón. null/'documento' = del documento, con su descripción y su
   * cantidad completas (el caso normal, no se marca). 'conteo_pagina' = el renglón no se
   * pudo leer del documento y entró con lo que se vio al mirar la página: descripción
   * cortada y cantidad que puede no ser la del documento. Se marca en la hoja.
   */
  origen?: RenglonOrigen | null;
  /** Página del documento donde está el renglón, para poder ir a mirarla. */
  origenPagina?: number | null;
  /** Una persona corrigió a mano la descripción, la unidad o la cantidad. */
  editadoManual?: boolean;
  editadoPor?: string | null;
  /** Nombre de quien lo corrigió, para poder decirlo en pantalla. */
  editadoPorNombre?: string | null;
  editadoAt?: string | null;
}

/** Marca/desmarca conceptos para cotizar (por concepto; la partida la resuelve
 *  el cliente expandiendo a sus hijos). Devuelve cuántos quedan seleccionados. */
export async function setQuoteSelection(
  quoteId: string,
  conceptIds: string[],
  cotizar: boolean,
): Promise<{ seleccionados: number }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/selection`, {
    method: 'POST',
    body: { conceptIds, cotizar },
  });
}

export async function listQuotes(projectId: string): Promise<Quote[]> {
  const r = await apiFetch<{ quotes: Quote[] }>(`/api/v1/projects/${projectId}/quotes`);
  return r.quotes;
}

export async function createQuote(
  projectId: string,
  body: { name: string; sourceFileId: string; options?: Record<string, unknown>; catalogIds?: string[] },
): Promise<{ quote: Quote; jobId: string | undefined }> {
  return await apiFetch(`/api/v1/projects/${projectId}/quotes`, {
    method: 'POST',
    body,
  });
}

export interface QuotePreview {
  totalConcepts: number;
  plan: string;
  currentUsage: number;
  planLimit: number | null;
  /** Saldo de recargas (packs) disponible — no expira, plan de precios OPUS jul 2026. */
  packBalance: number;
  /** `planLimit + packBalance` (null si ilimitado). */
  effectiveLimit: number | null;
  remaining: number | null;
  /** true si la cotización completa cabe en la cuota restante (incluye packs). */
  fits: boolean;
  /** El documento (PDF escaneado) no se leyó bien: extracción fragmentada →
   *  requiere análisis avanzado con IA de visión (aún no disponible). */
  needsVision: boolean;
  /** Páginas del documento a leer por imagen. null si no aplica. */
  visionPages: number | null;
  /** Costo estimado de leerlo con visión — SOLO staff Ecosoft; null en cliente. */
  visionCost: { usd: number } | null;
  /** Moneda en la que va a quedar el presupuesto (ISO 4217) o null si no se sabe. */
  monedaObra?: string | null;
  monedaOrigen?: 'contexto' | 'catalogos' | 'desconocida';
  /**
   * Problema de moneda que hay que decir ANTES de gastar. `CATALOGOS_EN_OTRA_MONEDA`
   * = el filtro por moneda deja la lista de catálogos en cero (todos los precios
   * saldrían de internet teniendo el catálogo cargado) y no se puede seguir;
   * `MONEDA_EN_CONFLICTO` = no hay moneda declarada y los catálogos están en varias.
   * `mensaje` ya viene redactado para mostrarse tal cual.
   */
  avisoMoneda?: {
    codigo: 'CATALOGOS_EN_OTRA_MONEDA' | 'MONEDA_EN_CONFLICTO';
    mensaje: string;
    monedaObra: string | null;
    monedasCatalogos: string[];
  } | null;
}

export async function previewQuote(
  projectId: string,
  sourceFileId: string,
): Promise<QuotePreview> {
  return await apiFetch<QuotePreview>(`/api/v1/projects/${projectId}/quotes/preview`, {
    method: 'POST',
    body: { sourceFileId },
  });
}

export async function setQuoteCatalogs(
  quoteId: string,
  catalogIds: string[],
): Promise<{ quote: Quote }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/catalogs`, {
    method: 'PATCH',
    body: { catalogIds },
  });
}

export interface QuoteCoverage {
  total: number;
  sampled: number;
  covered: number;
  coverage: number;
  gateMin: number;
}

/** Estimación de cobertura de los catálogos guardados sobre el listado ($0, sin IA). */
export async function getQuoteCoverage(quoteId: string): Promise<QuoteCoverage> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/coverage`);
}

/** Cambia el archivo fuente (catálogo de conceptos) de la cotización. */
export async function setQuoteSource(
  quoteId: string,
  sourceFileId: string,
): Promise<{ quote: Quote }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/source`, {
    method: 'PATCH',
    body: { sourceFileId },
  });
}

export async function regenerateQuote(
  quoteId: string,
  catalogIds: string[],
  options?: Record<string, unknown>,
  /**
   * El usuario ya vio que su bolsa no alcanza para toda la hoja y aceptó que se arme
   * hasta donde alcance. Sin esto el servidor responde 409 y no encola nada: preguntar
   * antes de gastar es justamente el punto.
   */
  confirmarParcial?: boolean,
): Promise<{ quote: Quote; jobId?: string }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/regenerate`, {
    method: 'POST',
    body: {
      catalogIds,
      ...(options != null ? { options } : {}),
      ...(confirmarParcial ? { confirmarParcial: true } : {}),
    },
  });
}

export async function getQuote(quoteId: string): Promise<Quote> {
  return await apiFetch<Quote>(`/api/v1/quotes/${quoteId}`);
}

// El acumulado de tokens/costo del tenant (`GET /api/v1/quotes/llm-stats`) ya NO se pide
// desde esta pantalla: nada lo mostraba (se guardaba en un estado que nadie leía) y es
// justo el dato que no va a la vista del cliente. Si algún día hace falta, va detrás de
// staff, no en la carga del presupuesto.

export async function getQuoteConcepts(
  quoteId: string,
  opts: { limit?: number; offset?: number; status?: string } = {},
): Promise<{ quoteId: string; total: number; items: QuoteConcept[] }> {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.offset != null) qs.set('offset', String(opts.offset));
  if (opts.status) qs.set('status', opts.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return await apiFetch(`/api/v1/quotes/${quoteId}/concepts${suffix}`);
}

export async function setQuoteControl(
  quoteId: string,
  control: QuoteControl,
): Promise<{ quote: Quote; jobId?: string }> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/control`, {
    method: 'PATCH',
    body: { control },
  });
}

export async function patchConcept(
  quoteId: string,
  conceptId: string,
  patch: {
    cantidad?: number;
    precio_unitario?: number;
    descripcion?: string;
    unidad?: string;
    nota?: string;
  },
): Promise<QuoteConcept> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/concepts/${conceptId}`, {
    method: 'PATCH',
    body: patch,
  });
}

/**
 * CORREGIR un renglón a mano: descripción, unidad y cantidad. Se manda el estado completo
 * de los tres campos; el servidor decide qué cambió y, si hace falta, le vuelve a buscar
 * el equivalente en los catálogos. No consume cuota.
 */
export async function editConcept(
  quoteId: string,
  conceptId: string,
  valores: EditarRenglonRequest,
): Promise<EditarRenglonResponse<QuoteConcept>> {
  return await apiFetch(`/api/v1/quotes/${quoteId}/concepts/${conceptId}/edit`, {
    method: 'PATCH',
    body: valores,
  });
}

export async function deleteQuote(quoteId: string): Promise<void> {
  await apiFetch(`/api/v1/quotes/${quoteId}`, { method: 'DELETE' });
}

export function quoteExcelUrl(quoteId: string): string {
  return `/api/v1/quotes/${quoteId}/excel`;
}
